import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Image,
  Nav,
  Row,
  Spinner,
} from "react-bootstrap";
import BreadcrumbSection from "components/BreadcrumbSection";
import { Icon } from "@iconify/react";
import { Link, useLocation } from "react-router-dom";
import IMAGES from "constants/images";
import CommonModal from "components/CommonModal";
import TextInput from "components/TextInput";
import { Sentry } from "react-activity";
import {
  getAllCommunityQuestions,
  getMyCommunityAnswers,
  submitCommunityAnswer,
  createCommunityQuestion,
  updateCommunityAnswer,
  deleteCommunityAnswer,
  deleteCommunityQuestion,
} from "api/CommunityService";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "context/UserContext";
import { useNavigate } from "react-router-dom";
import CollapsibleText from "components/CollapsibleText";
import Loader from "components/Loader";
import moment from "moment";

const sortOptions = [
  { label: "Most Recent", value: "most_recent" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

const SortDropdown = ({ selectedOption, setSelectedOption }) => {
  return (
    <Dropdown align="start">
      <Dropdown.Toggle
        variant="light"
        className="d-flex align-items-center rounded-pill px-3"
      >
        <Icon icon="solar:sort-linear" fontSize={19} className="me-2" />
        <span className="iconFull">{selectedOption.label}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {sortOptions.map((option, index) => (
          <Dropdown.Item key={index} onClick={() => setSelectedOption(option)}>
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const CommunityDetails = () => {
  const [activeTab, setActiveTab] = useState("Community Q&A");
  const [showAskModal, setShowAskModal] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [myAnswers, setMyAnswers] = useState([]);
  const [editingAnswerId, setEditingAnswerId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // You can adjust
  const [hasMore, setHasMore] = useState(false);
  const [answerPage, setAnswerPage] = useState(1);
  const [answerLimit] = useState(10);
  const [hasMoreAnswers, setHasMoreAnswers] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAnswerId, setDeletingAnswerId] = useState(null);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [selectedOption, setSelectedOption] = useState(sortOptions[0] || []);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const resource = location?.state?.resource;
  const productId = resource?._id;
  const productName = resource?.name;
  const { user } = useAuth();
  const fetchQuestions = async (resetPage = false) => {
    setSubmitLoader(true);
    setFetchLoader(true)
    if (!productId) return;

    const currentPage = resetPage ? 1 : page;
    const filter = selectedOption.value;
    const res = await getAllCommunityQuestions(
      filter,
      productId,
      currentPage,
      limit
    );

    if (res.success) {
      const newQuestions = res.body.data;
      const totalPages = res.body.total_pages;

      setQuestions((prev) =>
        resetPage ? newQuestions : [...prev, ...newQuestions]
      );
      setHasMore(currentPage < totalPages);
      setPage(currentPage + 1);
    }
    setSubmitLoader(false);
    setFetchLoader(false)
  };

  const fetchAnswers = async (resetPage = false) => {
    setFetchLoader(true)
    if (!productId) return;

    const currentPage = resetPage ? 1 : answerPage;
    const filter = selectedOption.value;
    const res = await getMyCommunityAnswers(
      filter,
      productId,
      currentPage,
      answerLimit
    );

    if (res.success) {
      const newAnswers = res.body.data;
      const totalPages = res.body.total_pages;

      setMyAnswers((prev) =>
        resetPage ? newAnswers : [...prev, ...newAnswers]
      );
      setHasMoreAnswers(currentPage < totalPages);
      setAnswerPage(currentPage + 1);
      setPageLoading(false);
      setFetchLoader(false)
    }
  };
  useEffect(() => {
    fetchQuestions(true);
    fetchAnswers(true);
  }, [productId, selectedOption,activeTab]);
  console.log('selectedOption', selectedOption)
  // console.log("selectedOption", );
  // Ask Question Validation
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(
      yup.object({
        question: yup.string().required("Question is required").min(10),
      })
    ),
  });

  const handleAskQuestion = async (data) => {
    setSubmitLoader(true);
    const res = await createCommunityQuestion({
      question: data.question,
      productId,
    });
    if (res.success) {
      reset();
      setShowAskModal(false);
      fetchQuestions(true);
      setSubmitLoader(false);
    } else {
      setSubmitLoader(false);
    }
  };

  // Answer Validation
  const {
    register: registerAnswer,
    handleSubmit: handleSubmitAnswerForm,
    formState: { errors: answerErrors },
    reset: resetAnswerForm,
    setValue,
  } = useForm({
    resolver: yupResolver(
      yup.object({
        answer: yup.string().required("Answer is required").min(5),
      })
    ),
  });

  const handleSubmitAnswer = async (data) => {
    let res;
    setSubmitLoader(true);
    if (editingAnswerId) {
      // If editing existing answer
      res = await updateCommunityAnswer({
        id: editingAnswerId,
        answer: data.answer,
      });
    } else {
      // Submitting a new answer
      res = await submitCommunityAnswer({
        questionId: currentQuestion._id,
        productId,
        answer: data.answer,
      });
    }

    if (res.success) {
      resetAnswerForm();
      setShowAnswerModal(false);
      setEditingAnswerId(null);
      // fetchQuestions(true);
      fetchAnswers(true);
      setSubmitLoader(false);
      if (!editingAnswerId && currentQuestion) {
        // New Answer Submit ➔ Update Questions List Locally

        setQuestions((prevQuestions) => {
          return prevQuestions.map((q) => {
            if (q._id === currentQuestion._id) {
              return {
                ...q,
                user_community_answers: [
                  res.body,
                  ...q.user_community_answers,
                  // Assuming your API returns the created answer
                ],
              };
            }
            return q;
          });
        });
      }
    } else {
      setSubmitLoader(false);
    }
  };

  const confirmDeleteAnswer = (id) => {
    setDeletingAnswerId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteAnswer = async () => {
    setSubmitLoader(true)
    const res = await deleteCommunityAnswer(deletingAnswerId);
    if (res.success) {
      setShowDeleteModal(false);
      setDeletingAnswerId(null);
      fetchQuestions(true);
      fetchAnswers(true);
    }
    setSubmitLoader(false)
  };

  const handleDeleteQuestion = async () => {
    setSubmitLoader(true);
    const res = await deleteCommunityQuestion(deletingQuestionId);
    if (res.success) {
      setShowDeleteModal(false);
      setDeletingQuestionId(null);
      fetchQuestions(true);
    }
    setSubmitLoader(false);
  };

  const toggleExpandAnswers = (id) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return pageLoading ? (
    <div className="d-flex justify-content-center align-items-center py-4" style={{minHeight: '100vh'}}>
      <Sentry color="#e91515" size={40} speed={1} animating={true} />
      {/* <Loader /> */}
    </div>
  ) : (
    <>
      <BreadcrumbSection
        breadcrumbs={[
          {
            title: productName,
            link: `/product-details/${resource?.slug}`,
            state: { resource: resource },
          },
          { title: "Community" },
        ]}
      />

      <section className="software_section py-5">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="software_header d-flex justify-content-between align-items-center mb-3">
                <h2>Community</h2>
                <SortDropdown
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                />
              </div>

              <div className="nav_Flex mb-4">
                <Nav variant="tabs" defaultActiveKey="Community Q&A">
                  {["Community Q&A", "My Answers"].map((tab) => (
                    <Nav.Item key={tab}>
                      <Nav.Link
                        eventKey={tab}
                        active={activeTab === tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                          backgroundColor:
                            activeTab === tab ? "#E91515" : "transparent",
                          color: activeTab === tab ? "white" : "black",
                        }}
                      >
                        {tab}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
                <Button
                  onClick={() => setShowAskModal(true)}
                  variant="info"
                  className="border-radius-100 px-4"
                >
                  <Icon icon="material-symbols:add-rounded" fontSize={22} /> Ask
                  Questions
                </Button>
              </div>

              {activeTab === "Community Q&A" &&

              <div>
                  {
    //               fetchLoader ?  <div className="loaderdiv">
    //   {/* <Sentry color="#e91515" size={40} speed={1} animating={true} /> */}
    //   <Loader />
    // </div>:  
     questions?.map((q, index) => {
                  const isExpanded = expandedQuestions[q._id];
                  const visibleAnswers = isExpanded
                    ? q.user_community_answers
                    : q.user_community_answers?.slice(0, 3);

                  return (
                    <div key={index} className="r_Card mb-4 ">
                      <div className="headerFLex gap-2">
                        <div
                          className="d-flex align-items-start gap-2 flex_col1 write_ans"
                          style={{ maxWidth: "80%" ,
                            wordBreak: 'break-word',
                           
                           }}
                        >
                          <h4 className="mb-0"  style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6', marginBottom: '1rem'}}>
                            Q. {q.question}{" "}
                            {q.userId?._id === user?._id && (
                              <span
                                className="badge bg-danger your_question_text"
                                style={{ fontSize: "13px" }}
                              >
                                Your Question
                              </span>
                            )}
                          </h4>
                        </div>

                        <div className="d-flex justify-content-center flex-column align-items-center gap-2  ">
                          {q.userId?._id === user?._id && (
                            <Button
                              variant="danger"
                              className="border-radius-100 d-flex gap-2 btn_padding header_profile3 "
                              onClick={() => {
                                setDeletingQuestionId(q._id);
                                setShowDeleteModal(true);
                              }}
                              style={{
                                padding: "5px 10px",
                                height: "auto",
                                alignItems: "center",
                              }}
                            >
                              <Icon
                                icon="mi:delete"
                                fontSize={17}
                                className=""
                              />
                              <span className=" ms-2"> 
                                Delete Question
                              </span>
                            </Button>
                          )}
                          {/* {q.userId?._id !== user?._id && ( */}
                          <Link
                            to="#"
                            style={{ flexShrink: 0 }}
                            
                            onClick={(e) => {
                              e.preventDefault();
                              if (q && q._id) {
                                setCurrentQuestion(q);
                                setShowAnswerModal(true);
                              }
                            }}
                            className={`${
                              q.userId?._id === user?._id
                                ? "ms-auto me-2"
                                : "ms-auto"
                            }`}
                          >
                            Write Your Answer
                          </Link>
                          {/* )} */}
                        </div>
                      </div>
                     <div className="d-flex justify-content-between align-items-center">
                     <p className="date_para mb-0">
                        {moment(q.createdAt).format('MMM DD, YYYY')}
                      </p>
                      {q.userId?._id === user?._id && (
                            <Button
                              variant="danger"
                              className="border-radius-100 d-flex gap-2 btn_padding show_mobile hAnswers"
                              onClick={() => {
                                setDeletingQuestionId(q._id);
                                setShowDeleteModal(true);
                              }}
                              style={{
                                // padding: "5px 10px",
                                // height: "auto",
                                // alignItems: "center",
                              }}
                            >
                              <Icon
                                icon="mi:delete"
                                fontSize={17}
                                style={{fontSize: '20px'}}
                                className=""
                              />
                              <span className="show_tab ms-2"> 
                                Delete Question
                              </span>
                            </Button>
                          )}
                     </div>

                      {visibleAnswers?.map((a, i) => (
                        <Card
                          key={i}
                          className={`${
                            i === visibleAnswers.length - 1
                              ? "pb-0 border-0"
                              : "border-bottom mb-3 pb-3"
                          }`}
                        >
                          <Card.Body>
                            <div
                              className="userFlex"
             
                             
                            >
                              <Image
                                               style={{ cursor: "pointer" }}
                               onClick={() => {
                                console.log("user", user);
                                const url =
                                  user?._id === a.userId?._id
                                    ? `/my-profile?tab=0`
                                    : `/public-profile/${a.userId?._id}`;
                                console.log("url", url);
                              navigate(url)
                              }}
                                src={a.userId?.profileUrl || IMAGES.User2_Img}
                                className="imgUser"
                                alt=""
                              />
                              <div className="userGrow" >
                                <Card.Title onClick={() => {
                                console.log("user", user);
                                const url =
                                  user?._id === a.userId?._id
                                    ? `/my-profile?tab=0`
                                    : `/public-profile/${a.userId?._id}`;
                                console.log("url", url);
                              navigate(url)
                              }} style={{cursor: 'pointer'}}>
                                  {a.userId?.displayName ? a.userId?.displayName : `${a.userId?.firstName} ${a.userId?.lastName}`}
                                </Card.Title>
                                <Card.Subtitle className="text-muted mb-0">
                                  {moment(a.createdAt).format('MMM DD, YYYY')}
                                </Card.Subtitle>
                              </div>
                            </div>
                            <Card.Text className="para"><CollapsibleText lines={5}>
                        <div dangerouslySetInnerHTML={{ __html: a.answer }} style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6', marginBottom: '1rem'}}></div>
                        </CollapsibleText></Card.Text>
                            {/* <Card.Text>{a.answer}</Card.Text> */}
                          </Card.Body>
                        </Card>
                      ))}

                      {q.user_community_answers?.length > 3 && (
                        <div className="text-center">
                          <Button
                            variant="outline-info"
                            className="border-radius-100 px-5"
                            onClick={() => toggleExpandAnswers(q._id)}
                          >
                            {isExpanded ? "Hide Answers" : "View All Answers"}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {questions?.length === 0 && (
                <Col xs={12}>
                <div className="text-center py-5">
                  <Image
                    src={IMAGES.emptyState}
                    alt=""
                    height="100px"
                    width="100px"
                    className="mb-4"
                    // className="imgUser"
                  />
                  <p className="text-center text-muted">
                    No Questions found.
                  </p>
                </div>
              </Col>
                )}
              </div>}
             
              {activeTab === "Community Q&A"  && (
                <>
                  {hasMore && (
                    <div className="text-center justify-content-center d-flex" >
                      {/* <Button
                        disabled={fetchLoader}
                        style={{ width: fetchLoader && "250px" }}
                        variant="outline-info"
                        className="border-radius-100 px-5"
                        onClick={() => fetchQuestions()}
                      >
                        {fetchLoader ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          "Load More Questions"
                        )}
                      </Button> */}

{hasMore && <Button
                                              disabled={fetchLoader}
                    className="added_width1"
                    variant="outline-primary"
                    onClick={() => fetchQuestions()}
             
                    style={{
                      width: fetchLoader && "250px",
                      borderRadius: "50px",
                      borderColor: "#0071E3",
                      color: "#0071E3",
                    }}
                  >
                    {fetchLoader ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Load More Questions"
                    )}
                  </Button>}
                    </div>
                  )}
                </>
              )}

              {activeTab === "My Answers" &&
              <div>
                 {
              //    fetchLoader ?  <div className="loaderdiv">
              //   {/* <Sentry color="#e91515" size={40} speed={1} animating={true} /> */}
              //   <Loader />
              // </div> :
               myAnswers?.map((qa, index) => (
                  <div key={index} className="r_Card mb-4 my_answer_card">
                    <div className="headerFLex">
                      <h4 className="mb-2" style={{whiteSpace: 'pre-wrap', lineHeight: '1.6', marginBottom: '1rem'}}>{qa.questionId?.question}</h4>
                   <div className="d-flex align-items-center  header_profile3"> 
                   <Button
                        variant="info"
                        className="border-radius-100 ms-auto me-3 hAnswers d-flex align-items-center gap-2"
                        onClick={() => {
                          setCurrentQuestion(qa.questionId);
                          setEditingAnswerId(qa._id); // set editing
                          setValue("answer", qa.answer);
                          setShowAnswerModal(true);
                        }}
                      >
                        <Icon
                          icon="lucide:edit"
                          fontSize={16}
                          className=" "
                          style={{fontSize: '20px'}}
                        />
                        <span className="show_desktop me-2">Edit Answer</span>
                      </Button>
                      <Button
                        variant="danger"
                        className="border-radius-100 hAnswers d-flex align-items-center gap-2"
                        onClick={() => confirmDeleteAnswer(qa._id)}
                      >
                        <Icon icon="mi:delete" fontSize={17} className="" style={{fontSize: '20px'}} />
                        <span className="show_desktop me-2">Delete Answer</span>
                      </Button>
                   </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                    <p className="date_para mb-0">
                      {moment(qa.createdAt).format('MMM DD, YYYY')}
                    </p>
                    <div className="d-flex align-items-center gap-2 header_profile2"> 
                   <Button
                        variant="info"
                        className="border-radius-100 ms-auto hAnswers d-flex align-items-center gap-2"
                        onClick={() => {
                          setCurrentQuestion(qa.questionId);
                          setEditingAnswerId(qa._id); // set editing
                          setValue("answer", qa.answer);
                          setShowAnswerModal(true);
                        }}
                      >
                        <Icon
                          icon="lucide:edit"
                          fontSize={16}
                          className=" "
                          style={{fontSize: '16px'}}
                        />
                        <span className="show_desktop me-2">Edit Answer</span>
                      </Button>
                      <Button
                        variant="danger"
                        className="border-radius-100 hAnswers d-flex align-items-center gap-2"
                        onClick={() => confirmDeleteAnswer(qa._id)}
                      >
                        <Icon icon="mi:delete" fontSize={17} className="" style={{fontSize: '16px'}} />
                        <span className="show_desktop me-2">Delete Answer</span>
                      </Button>
                   </div>
                    </div>
                  
                    <Card className="mb-0 border-0 pb-0">
                      <Card.Body>
                        <Card.Text className="para"><CollapsibleText lines={5}>
                        <div dangerouslySetInnerHTML={{ __html: qa.answer }} style={{whiteSpace: 'pre-wrap',wordBreak: 'break-word', lineHeight: '1.6', marginBottom: '1rem'}}></div>
                        </CollapsibleText></Card.Text>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
                {myAnswers?.length === 0 && (
                <Col xs={12}>
                <div className="text-center py-5">
                  <Image
                    src={IMAGES.emptyState}
                    alt=""
                    height="100px"
                    width="100px"
                    className="mb-4"
                    // className="imgUser"
                  />
                  <p className="text-center text-muted">
                    No Answers found.
                  </p>    
                </div>
              </Col>
                )}
              </div>}
              {activeTab === "My Answers" && (
                <>
                  {hasMoreAnswers && (
                    <div className="text-center d-flex justify-content-center">
                      {/* <Button 
                        disabled={fetchLoader}
                        style={{ width: fetchLoader && "250px" }}
                        variant="outline-secondary"
                        onClick={() => fetchAnswers()}
                      >
                       {fetchLoader ?   <Spinner animation="border" size="sm" />:    "Load More Answers"}
                      </Button> */}
                      <Button
                                              disabled={fetchLoader}
                    className="added_width1"
                    variant="outline-primary"
                    onClick={() => fetchAnswers()}
             
                    style={{
                      width: fetchLoader && "250px",
                      borderRadius: "50px",
                      borderColor: "#0071E3",
                      color: "#0071E3",
                    }}
                  >
                    {fetchLoader ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Load More Answers"
                    )}
                  </Button>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Ask Question Modal */}
      <CommonModal
        title="Ask Question"
        para="Have a question? Ask the community and get insights from experts, vendors, and experienced users."
        body={
          <form onSubmit={handleSubmit(handleAskQuestion)}>
            <TextInput
              labelText="Question"
              textarea
              rows={6}
              placeholder="Type Your Question..."
              maxLength={500}
              {...register("question")}
              errorMessage={errors.question?.message}
            />
            <Button
              disabled={submitLoader}
              type="submit"
              variant="info"
              className="w-100 border-radius-100 mt-3"
            >
              {submitLoader ? (
                <Spinner animation="border" size="sm" />
              ) : (
                " Submit"
              )}
            </Button>
          </form>
        }
        show={showAskModal}
        onClose={() => {
          setShowAskModal(false);
          reset();
        }}
      />

      {/* Write/Edit Answer Modal */}
      <CommonModal
        title="Write Answer"
        para="Share your insights or solutions by writing an answer for this question."
        body={
          <form onSubmit={handleSubmitAnswerForm(handleSubmitAnswer)}>
            <TextInput
              labelText="Answer"
              textarea
              rows={6}
              maxLength={1000}
              placeholder="Write Your Answer..."
              {...registerAnswer("answer")}
              errorMessage={answerErrors.answer?.message}
            />
            <Button
              disabled={submitLoader}
              type="submit"
              variant="info"
              className="w-100 border-radius-100 mt-3"
            >
              {submitLoader ? (
                <Spinner animation="border" size="sm" />
              ) : (
                " Submit"
              )}
            </Button>
          </form>
        }
        show={showAnswerModal}
        onClose={() => {
          setShowAnswerModal(false);
          resetAnswerForm();
        }}
      />

      <CommonModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingAnswerId(null);
          setDeletingQuestionId(null);
        }}
        title="Confirm Deletion"
        para="Are you sure you want to delete this item? This action cannot be undone."
        body={
          <div className="d-flex justify-content-center gap-2 mt-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingAnswerId(null);
                setDeletingQuestionId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={submitLoader}
              variant="danger"
              style={{ width: submitLoader && "85px" }}
              onClick={() => {
                if (deletingAnswerId) {
                  handleDeleteAnswer();
                } else if (deletingQuestionId) {
                  handleDeleteQuestion();
                }
              }}
            >
              {submitLoader ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        }
      />
    </>
  );
};

export default CommunityDetails;
